import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { orderBy, has, set, split, get } from 'lodash/fp';
import { Table } from 'rsuite';
import Pagination from '../../Pagination';
import AutoHeightTable from '../../AutoHeightTable';
import DeleteInventoryItemModal from '../DeleteItemModal';
import { ActionCell, TagsCell, LinkCell, ImageLinkCell } from './CustomCells';
import WeightCell from '../../widgets/custom-cell/Weight';
import DateCell from '../../widgets/custom-cell/Date';
import {
  ItemType,
  PackageType
} from '../../../constants/inventory/InventoryTypes';
import PaginationType from '../../../interfaces/PaginationType';
import CustomHeaderCell from '../../widgets/custom-cell/HeaderCell';
import FoodStopLightCell from '../../widgets/custom-cell/FoodStopLightCell';

const { Column, HeaderCell, Cell } = Table;

class InventoryItemList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isInit: false,
      showConfirmDelete: false,
      itemToDelete: null
    };
  }

  componentDidMount() {
    const { isInit } = this.state;
    const { onInit, query } = this.props;
    if (!isInit) {
      this.setState({ isInit: true }, () => onInit(query));
    }
  }

  componentDidUpdate({ isDeleted: prevIsDeleted }) {
    const { isDeleted } = this.props;
    if (!prevIsDeleted && isDeleted) {
      this.onItemDeleted();
    }
  }

  getData() {
    const { items } = this.props;
    const { sortColumn, sortType } = this.state;
    return orderBy(sortColumn, sortType, items);
  }

  handleSortColumn = (sortColumn, sortType) => {
    const { onSort, query } = this.props;
    onSort(sortColumn, sortType, query);
  };

  onItemSaved = () => {
    this.handleCloseCreateModal();
  };

  onItemDeleted = () => {
    this.handleCloseDeleteModal();
  };

  handleOpenDeleteModal = itemToDelete => {
    this.setState({ showConfirmDelete: true, itemToDelete });
  };

  handleCloseDeleteModal = () => {
    this.setState({ showConfirmDelete: false });
  };

  handleConfirmDelete = () => {
    const { itemToDelete } = this.state;
    const { onDelete } = this.props;
    onDelete(itemToDelete);
  };

  handleChangePage = page => {
    const { onChangePage, loadedPages, query } = this.props;
    const isPageLoaded = has(page, loadedPages);
    const nextPageQuery = set('page', page, query);
    onChangePage(nextPageQuery, isPageLoaded);
  };

  handleChangeLimit = limit => {
    const { onChangeLimit, query } = this.props;
    const newQuery = { ...query, limit, page: 1 };
    onChangeLimit(newQuery);
  };

  renderPagination() {
    const { pagination } = this.props;
    return (
      <Pagination
        page={pagination.currentPage}
        limit={pagination.limit}
        total={pagination.totalCount}
        onChangePage={this.handleChangePage}
        onChangeLimit={this.handleChangeLimit}
      />
    );
  }

  render() {
    const { type, isFetching, isDeleting, query } = this.props;
    const [sortColumn, sortType] = split(':', get('order', query));
    const { showConfirmDelete, itemToDelete } = this.state;
    return (
      <div>
        <AutoHeightTable
          reduceHeight={540}
          loading={isFetching}
          data={this.getData()}
          sortColumn={sortColumn}
          sortType={sortType}
          onSortColumn={this.handleSortColumn}
          bordered
        >
          <Column width={60} sortable>
            <CustomHeaderCell>Id</CustomHeaderCell>
            <LinkCell dataKey="id" />
          </Column>
          <Column width={70} align="center">
            <CustomHeaderCell>Photo</CustomHeaderCell>
            <ImageLinkCell dataKey="photo" />
          </Column>
          <Column width={150} sortable resizable>
            <CustomHeaderCell>Name</CustomHeaderCell>
            <LinkCell dataKey="name" />
          </Column>
          <Column width={250} resizable>
            <CustomHeaderCell>Description</CustomHeaderCell>
            <Cell dataKey="description" />
          </Column>
          <Column width={60} align="right">
            <CustomHeaderCell>Qty</CustomHeaderCell>
            <Cell dataKey="quantity" />
          </Column>
          <Column width={65} align="right" sortable>
            <CustomHeaderCell>Price</CustomHeaderCell>
            <Cell dataKey="price" />
          </Column>
          <Column width={95} align="right" sortable>
            <CustomHeaderCell>Weight (lb)</CustomHeaderCell>
            <WeightCell dataKey="weight" />
          </Column>
          <Column width={150}>
            <CustomHeaderCell>Tags</CustomHeaderCell>
            <TagsCell dataKey="tags" />
          </Column>
          {ItemType === type && (
            <Column width={60} align="center">
              <CustomHeaderCell>TNI</CustomHeaderCell>
              <FoodStopLightCell dataKey="stoplight" />
            </Column>
          )}
          <Column width={120} sortable>
            <CustomHeaderCell>Date Created</CustomHeaderCell>
            <DateCell dataKey="createdAt" />
          </Column>
          <Column width={200}>
            <HeaderCell align="center">Action</HeaderCell>
            <ActionCell
              dataKey="id"
              align="center"
              handleDelete={this.handleOpenDeleteModal}
            />
          </Column>
        </AutoHeightTable>

        {this.renderPagination()}

        {itemToDelete && (
          <DeleteInventoryItemModal
            show={showConfirmDelete}
            onClose={this.handleCloseDeleteModal}
            onConfirm={this.handleConfirmDelete}
            item={itemToDelete}
            isDeleting={isDeleting}
          />
        )}
      </div>
    );
  }
}

InventoryItemList.propTypes = {
  type: PropTypes.oneOf([ItemType, PackageType]).isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  query: PropTypes.shape({}).isRequired,
  loadedPages: PropTypes.shape({}).isRequired,
  pagination: PaginationType.isRequired,
  isFetching: PropTypes.bool.isRequired,
  isDeleting: PropTypes.bool.isRequired,
  isDeleted: PropTypes.bool.isRequired,
  onInit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired,
  onChangePage: PropTypes.func.isRequired,
  onChangeLimit: PropTypes.func.isRequired
};

export default InventoryItemList;
